import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { AssignmentTable } from '@yandex-int/k-common/typings';
import { createSelector } from 'reselect';

import * as classSelectors from 'common.components/classes/selectors';
import * as lessonAssignmentSelectors from 'common.components/lesson-view.assignment/selectors';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import * as lessonUtils from 'common.components/lesson/utils';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';
import { getIsLessonCloseAvailable } from 'utils/lesson';

import { getSearchFromProps } from 'utils/selectors/selectors';
import { ACTION_BUTTON_MODE } from './action-button-mode';

export const getActionButtonMode = createSelector(
    [
        lessonSelectors.getLesson,
        lessonSelectors.getLessonStatus,
        lessonSelectors.getLessonAssignmentDate,
        lessonViewSelectors.getShouldShowAssignmentSettings,
        lessonSelectors.getIsRepeatableLesson,
        lessonViewSelectors.getIsDisabledAssignment,
        getSearchFromProps,
    ],
    // eslint-disable-next-line complexity
    (
        lesson,
        lessonStatus,
        dateAssignment,
        shouldShowAssignmentSettings,
        isRepeatableLesson: boolean,
        isDisabledAssignment,
        search
        // eslint-disable-next-line max-params
    ): ACTION_BUTTON_MODE | null => {
        const isTeacherFromMes = getIsTeacherFromMes(search);
        if (!lesson?.id) {
            return null;
        }

        if (lessonStatus && lessonUtils.isLessonCompleted(lessonStatus)) {
            return ACTION_BUTTON_MODE.LESSON_COMPLETED;
        }

        if (isDisabledAssignment) {
            return ACTION_BUTTON_MODE.ASSIGNMENT_DISABLED;
        }

        const isLessonInProgress = lessonStatus ? lessonUtils.isLessonInProgress(lessonStatus) : false;

        if (getIsLessonCloseAvailable(isLessonInProgress, isRepeatableLesson)) {
            return ACTION_BUTTON_MODE.FORCE_COMPLETE_LESSON;
        }

        if (isTeacherFromMes) {
            return ACTION_BUTTON_MODE.ASSIGN_IN_EXTERNAL_LMS;
        }

        if (dateAssignment && shouldShowAssignmentSettings) {
            return ACTION_BUTTON_MODE.REASSIGN_LESSON;
        }

        if (dateAssignment) {
            return ACTION_BUTTON_MODE.EDIT_ASSIGNMENT;
        }

        return ACTION_BUTTON_MODE.ASSIGN_LESSON;
    }
);

export const getCanAssignLesson = createSelector(
    [
        lessonSelectors.getIsEmptyLesson,
        lessonAssignmentSelectors.getExternalLmsAvailableDatesLoadingStatus,
        lessonViewSelectors.getShouldAssignInExternalLms,
        lessonSelectors.getIsLessonContentEditable,
    ],
    (isEmpty, lmsLoadingStatus, shouldAssignInExternalLms, isLessonContentEditable) => {
        if (shouldAssignInExternalLms && !isLessonContentEditable) {
            return false;
        }

        return !isEmpty && ![LOADING_STATUSES.LOADING, LOADING_STATUSES.ERROR].includes(lmsLoadingStatus);
    }
);

export const getStudentsTable = createSelector(
    [lessonAssignmentSelectors.getSelectedStudents, classSelectors.getStudents],
    (selectedStudents, allStudents) => {
        return (
            allStudents?.reduce((state: AssignmentTable, { id }) => {
                if (!selectedStudents.includes(id)) {
                    state[id] = [];
                }

                return state;
            }, {}) || {}
        );
    }
);
